import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  marketEvents,
  TIMEZONES,
  type Category,
  type ImpactLevel,
  type MarketEvent,
} from '@/mocks/marketCalendar';
import { toUtcMinutes } from './calculations';

const WATCH_KEY = 'forex-watch-list';

export type EventStatus = 'upcoming' | 'now' | 'released';

export function useMarketClock() {
  const [now, setNow] = useState(() => Date.now());
  const [timezoneId, setTimezoneId] = useState('local');
  const [dayOffset, setDayOffset] = useState(0);
  const [currencyFilter, setCurrencyFilter] = useState<string>('all');
  const [impactFilter, setImpactFilter] = useState<ImpactLevel | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<Category | 'all'>('all');
  const [watchList, setWatchList] = useState<string[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  // tick every second
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // load watch list from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(WATCH_KEY);
      if (raw) setWatchList(JSON.parse(raw) as string[]);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleWatch = useCallback((code: string) => {
    setWatchList((prev) => {
      const next = prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code];
      try {
        localStorage.setItem(WATCH_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const timezone = useMemo(
    () => TIMEZONES.find((t) => t.id === timezoneId) ?? TIMEZONES[0],
    [timezoneId],
  );

  const offsetMinutes = useMemo(() => {
    if (timezone.offset === 'local') return -new Date().getTimezoneOffset();
    return timezone.offset;
  }, [timezone]);

  // NOW as minute-of-day (with fractional seconds) in the SELECTED timezone
  const nowDisplayMinutes = useMemo(() => {
    const shifted = new Date(now + offsetMinutes * 60000);
    return shifted.getUTCHours() * 60 + shifted.getUTCMinutes() + shifted.getUTCSeconds() / 60;
  }, [now, offsetMinutes]);

  // NOW as minute-of-day in UTC (for session/overlap status)
  const nowUtcMinutes = useMemo(() => {
    const d = new Date(now);
    return d.getUTCHours() * 60 + d.getUTCMinutes() + d.getUTCSeconds() / 60;
  }, [now]);

  // convert UTC minute-of-day -> display minute-of-day in selected timezone
  const toDisplay = useCallback(
    (utcMins: number) => ((utcMins + offsetMinutes) % 1440 + 1440) % 1440,
    [offsetMinutes],
  );

  // events for the selected day (raw, before filters)
  const dayEvents = useMemo(
    () =>
      marketEvents
        .filter((e) => e.dayOffset === dayOffset)
        .sort((a, b) => toUtcMinutes(a.time) - toUtcMinutes(b.time)),
    [dayOffset],
  );

  // full filtered list for the selected day
  const filteredEvents = useMemo(() => {
    return dayEvents.filter((e) => {
      if (currencyFilter !== 'all' && e.currency !== currencyFilter) return false;
      if (impactFilter !== 'all' && e.impact !== impactFilter) return false;
      if (categoryFilter !== 'all' && e.category !== categoryFilter) return false;
      return true;
    });
  }, [dayEvents, currencyFilter, impactFilter, categoryFilter]);

  // events for the selected day but honouring the WATCH list too (for planner + watch emphasis)
  const watchedEvents = useMemo(() => {
    if (watchList.length === 0) return filteredEvents;
    return filteredEvents.filter((e) => watchList.includes(e.currency));
  }, [filteredEvents, watchList]);

  const statusOf = useCallback(
    (e: MarketEvent): EventStatus => {
      if (e.actual !== null) return 'released';
      const ts = (() => {
        const d = new Date();
        const day = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + e.dayOffset));
        day.setUTCHours(0, 0, 0, 0);
        return day.getTime() + toUtcMinutes(e.time) * 60000;
      })();
      const diff = ts - now;
      if (diff > 0) return 'upcoming';
      // within a 10-minute window after the event = "now"
      if (Math.abs(diff) <= 10 * 60000) return 'now';
      return 'released';
    },
    [now],
  );

  const selectedEvent = useMemo(
    () => marketEvents.find((e) => e.id === selectedEventId) ?? null,
    [selectedEventId],
  );

  return {
    now,
    timezone,
    timezoneId,
    setTimezoneId,
    offsetMinutes,
    nowDisplayMinutes,
    nowUtcMinutes,
    toDisplay,
    dayOffset,
    setDayOffset,
    dayEvents,
    filteredEvents,
    watchedEvents,
    currencyFilter,
    setCurrencyFilter,
    impactFilter,
    setImpactFilter,
    categoryFilter,
    setCategoryFilter,
    watchList,
    toggleWatch,
    selectedEvent,
    setSelectedEventId,
    statusOf,
  };
}

export type MarketClock = ReturnType<typeof useMarketClock>;